import { useQuery } from '@tanstack/react-query';
import type { PowertrainType, Vehicle } from 'fuel-benchmark-contracts';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { formatDay } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { powertrainTone, vehicleTitle } from './vehicle-display';
import { VehicleSheet } from './VehicleSheet';

/** The catalogue is small; one page of 100 keeps client-side sorting honest without a second request. */
const PAGE_SIZE = 100;

export const VehiclesListPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [powertrainType, setPowertrainType] = useState('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const attributeOptions = useQuery(orpc.vehicles.attributeOptions.queryOptions());

  const vehicles = useQuery(
    orpc.vehicles.list.queryOptions({
      input: {
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: 'code',
        sortDirection: 'asc',
        search: search.trim() === '' ? undefined : search.trim(),
        segmentId: segmentId === '' ? undefined : segmentId,
        manufacturer: manufacturer === '' ? undefined : manufacturer,
        powertrainType: powertrainType === '' ? undefined : (powertrainType as PowertrainType),
      },
    }),
  );

  const segmentOptions: SelectOption[] = useMemo(
    () => (attributeOptions.data?.segments ?? []).map((segment) => ({ value: segment.id, label: segment.name })),
    [attributeOptions.data],
  );

  const manufacturerOptions: SelectOption[] = useMemo(
    () => (attributeOptions.data?.manufacturers ?? []).map((value) => ({ value, label: value })),
    [attributeOptions.data],
  );

  const powertrainOptions: SelectOption[] = useMemo(
    () => (attributeOptions.data?.powertrainTypes ?? []).map((value) => ({ value, label: t(`enums.powertrain.${value}`) })),
    [attributeOptions.data, t],
  );

  const columns: DataTableColumns<Vehicle> = useMemo(
    () => [
      {
        accessorKey: 'code',
        header: t('vehicles.columns.code'),
        cell: ({ row }) => <span className="font-mono text-xs text-foreground">{row.original.code}</span>,
      },
      {
        id: 'vehicle',
        accessorFn: (vehicle) => vehicleTitle(vehicle),
        header: t('vehicles.columns.vehicle'),
        cell: ({ row }) => (
          <InfoCell
            title={vehicleTitle(row.original)}
            subline={row.original.vin}
          />
        ),
      },
      {
        accessorKey: 'segmentName',
        header: t('vehicles.columns.segment'),
      },
      {
        accessorKey: 'powertrainType',
        header: t('vehicles.columns.powertrain'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.powertrain.${row.original.powertrainType}`)}
            tone={powertrainTone(row.original.powertrainType)}
          />
        ),
      },
      {
        accessorKey: 'modelYear',
        header: t('vehicles.columns.modelYear'),
      },
      {
        accessorKey: 'createdAt',
        header: t('vehicles.columns.created'),
        cell: ({ row }) => <span className="text-muted-foreground">{formatDay(row.original.createdAt)}</span>,
      },
    ],
    [t],
  );

  const hasFilters = search !== '' || segmentId !== '' || manufacturer !== '' || powertrainType !== '';

  const clearFilters = (): void => {
    setSearch('');
    setSegmentId('');
    setManufacturer('');
    setPowertrainType('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('vehicles.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('vehicles.subtitle')}</p>
        </div>
        <Button onClick={() => setIsSheetOpen(true)}>
          <Plus className="size-4" />
          {t('vehicles.add')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('vehicles.searchPlaceholder')}
          className="max-w-xs"
          aria-label={t('vehicles.searchPlaceholder')}
        />
        <Select
          value={segmentId}
          onChange={(event) => setSegmentId(event.target.value)}
          options={segmentOptions}
          placeholder={t('vehicles.allSegments')}
          aria-label={t('vehicles.allSegments')}
          className="w-auto min-w-40"
        />
        <Select
          value={manufacturer}
          onChange={(event) => setManufacturer(event.target.value)}
          options={manufacturerOptions}
          placeholder={t('vehicles.allManufacturers')}
          aria-label={t('vehicles.allManufacturers')}
          className="w-auto min-w-40"
        />
        <Select
          value={powertrainType}
          onChange={(event) => setPowertrainType(event.target.value)}
          options={powertrainOptions}
          placeholder={t('vehicles.allPowertrains')}
          aria-label={t('vehicles.allPowertrains')}
          className="w-auto min-w-40"
        />
        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
          >
            {t('actions.clearFilters')}
          </Button>
        ) : null}
      </div>

      {vehicles.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {t('errors.loadFailed')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={vehicles.data?.items ?? []}
        isLoading={vehicles.isPending}
        emptyMessage={t('vehicles.empty')}
        onRowClick={(vehicle) => navigate(`/vehicles/${vehicle.id}`)}
      />

      <VehicleSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        segmentOptions={segmentOptions}
        onSaved={(vehicleId) => navigate(`/vehicles/${vehicleId}`)}
      />
    </div>
  );
};
